import * as React from "react";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Calendar } from "@/v2/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/v2/components/ui/popover";
import { dateFromKey, formatLongDate, toDateKey } from "../schedulerUtils";

interface Props {
  /** "YYYY-MM-DD" */
  date: string;
  onSelectDate: (date: string) => void;
  onShift: (days: number) => void;
}

/** Single-date picker for the timeline: a calendar popover plus day-at-a-time
 *  prev/next, unlike the range picker the /vod filters use elsewhere. */
export function SchedulerDateSelector({ date, onSelectDate, onShift }: Props) {
  const [open, setOpen] = React.useState(false);
  const selected = dateFromKey(date);

  return (
    <div className="flex items-center" style={{ gap: 8 }}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-9 items-center gap-2 rounded-md border border-input bg-card px-3 text-[13px] font-medium text-foreground hover:bg-accent"
          >
            <CalendarIcon size={14} className="shrink-0 text-muted-foreground" />
            {formatLongDate(date)}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="v2 w-auto p-0">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={(next) => {
              if (!next) return;
              onSelectDate(toDateKey(next));
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        aria-label="Previous day"
        className="flex h-9 w-9 items-center justify-center rounded-md border border-input bg-card hover:bg-accent"
        onClick={() => onShift(-1)}
      >
        <ChevronLeft size={16} />
      </button>
      <button
        type="button"
        aria-label="Next day"
        className="flex h-9 w-9 items-center justify-center rounded-md border border-input bg-card hover:bg-accent"
        onClick={() => onShift(1)}
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
